import styled, { css } from 'styled-components';
import ReactQuill, { Quill } from 'react-quill-new';
import React, { useRef, useMemo, useState } from 'react';

import { cn } from '@epoch-ui/lib/utils';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { ERROR_RED } from 'constants/colours';
import 'react-quill-new/dist/quill.snow.css';
import { AITriggerModalButton } from 'components/common/AIModals/AITriggerModalButton';
import { EditAIDescriptionToolTip } from 'components/common/AIModals/editAIDescrption/EditAIDescriptionToolTip';

import { fromQuillHtml, toQuillHtml } from './editorHtml';

const StyledEditor = styled(ReactQuill)<{
  error: boolean;
  disabled: boolean;
  maxWidth?: boolean;
}>(
  ({ error, disabled, maxWidth }) => css`
    opacity: ${disabled && '0.5'};
    .ql-container.ql-snow,
    .ql-toolbar.ql-snow {
      border-color: ${error && ERROR_RED};
      pointer-events: ${disabled && 'none'};
    }
    word-break: normal;
    max-width: ${maxWidth && '630px;'};
    .ql-editor {
      max-height: 200px;
      overflow-y: auto;
    }
  `
);

export interface RichTextEditorProps {
  displayImages?: boolean;
  uploadImage?: (file: File) => Promise<string>;
  error: boolean;
  disabled: boolean;
  style?: React.CSSProperties;
  readOnly?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (html: string) => void;
  onBlur?: () => void;
  maxWidth?: boolean;
  isDescriptionEmpty?: boolean;
  handleAIModalOpen?: () => void;
  showAIModal?: boolean;
  handleEditDescription?: (editOptions: string[], editInstruction?: string) => void;
  compactToolbar?: boolean;
}

const RichTextEditor: React.FC<React.PropsWithChildren<RichTextEditorProps>> = (
  richtextprops
): React.ReactElement => {
  const fileInput = React.useRef<HTMLInputElement>(null);
  const rte = useRef<ReactQuill>(null);
  const toolbarId = React.useId();
  const { showToast } = useToaster();

  const [showAIEditDropdown, setShowAIEditDropdown] = useState(false);

  const {
    uploadImage,
    displayImages,
    error,
    disabled,
    maxWidth,
    isDescriptionEmpty,
    handleAIModalOpen,
    showAIModal,
    handleEditDescription,
    compactToolbar = false,
    value = '',
    onChange,
    ...props
  } = richtextprops as RichTextEditorProps;

  const aiTriggerLabel = isDescriptionEmpty ? 'Suggest' : 'Edit';

  const modules = useMemo<any>(() => {
    return {
      clipboard: {
        matchers: [
          /**
           * Handler to remove image tag on pasting.
           * Note: This gets triggered on initial render when we put saved description into editor.
           */
          [
            'IMG',
            (node: any, delta: any): any => {
              const src = delta.ops[0].insert.image;

              /**
               * Check if original description value has same image src string.
               * If it does, we have to render it.
               */
              if (value.includes(src)) {
                return delta;
              }
              // If user is trying to paste in image, replace it with empty text.
              const Delta = Quill.import('delta');
              return new Delta().insert('');
            }
          ]
        ]
      },
      toolbar: {
        // Workaround to escape special characters in id generated by React.useId()
        // https://stackoverflow.com/a/73894097
        container: `[id="${toolbarId}"]`,
        handlers: {
          image: (): void => {
            if (fileInput.current) fileInput.current.click();
          }
        }
      }
    };
  }, []);

  const buttonRef = useRef<HTMLDivElement>(null);
  return (
    <>
      <input
        ref={fileInput}
        className="d-none"
        type="file"
        accept="image/png, image/jpeg"
        onChange={async (event): Promise<void> => {
          const { target } = event;
          const {
            validity,
            files: [file]
          } = target as any;

          if (file && validity.valid) {
            if (file.size > 5242880) {
              showToast('The file uploaded is over 5mb', { variant: ToasterVariant.ERROR });
              const input = fileInput.current;
              if (input) input.value = '';
            }
            if (!['image/png', 'image/jpeg'].includes(file.type)) {
              showToast('Invalid file type', { variant: ToasterVariant.ERROR });
              const input = fileInput.current;
              if (input) input.value = '';
            } else {
              try {
                if (uploadImage) {
                  const url = await uploadImage(file);
                  if (rte.current) {
                    const editor = rte.current.getEditor();
                    editor.insertEmbed(editor.getSelection() as any as number, 'image', url);
                  }
                }
              } catch (e) {
                console.error(e);
              }
            }
          }
        }}
      />
      <div className="text-editor">
        <div id={toolbarId} className="tw-flex !tw-h-[42px]">
          <div className="tw-flex tw-w-full tw-flex-row tw-items-center">
            <div className={cn('tw-mr-3', compactToolbar && 'tw-mr-1')}>
              <select className="ql-header" defaultValue="">
                <option value="1" aria-label="size-small" />

                <option value="2" aria-label="size-small" />
                <option value="3" aria-label="size-small" />
                <option value="">Normal</option>
              </select>
            </div>

            <div className={cn('tw-mr-3', compactToolbar && 'tw-mr-1')}>
              <button className="ql-bold" type="button" aria-label="Bold" />
              <button className="ql-italic" type="button" aria-label="Italic" />
              <button className="ql-underline" type="button" aria-label="Italic" />
            </div>

            <div className={cn('tw-mr-3', compactToolbar && 'tw-mr-1')}>
              <button className="ql-list" value="ordered" aria-label="list-ordered" type="button" />
              <button className="ql-list" value="bullet" aria-label="list-bullet" type="button" />
            </div>

            <div className={cn('tw-mr-3', compactToolbar && 'tw-mr-1')}>
              {displayImages ? (
                <button className="ql-image" aria-label="image" type="button" />
              ) : null}
              <button className="ql-link" aria-label="link" type="button" />
            </div>

            {/* Button to open up AI description generator */}
            {showAIModal && handleAIModalOpen ? (
              <div className="tw-relative tw-ml-auto">
                {/* Tooltip */}
                {showAIEditDropdown && (
                  <EditAIDescriptionToolTip
                    setShowAIEditDropdown={(): void => {
                      setShowAIEditDropdown(false);
                    }}
                    buttonRef={buttonRef}
                    handleEditDescription={(editOptions, editInstruction): void => {
                      if (handleEditDescription) {
                        handleEditDescription(editOptions, editInstruction);
                      }
                    }}
                  />
                )}
                <div ref={buttonRef}>
                  <AITriggerModalButton
                    handleClick={(): void => {
                      if (isDescriptionEmpty) {
                        handleAIModalOpen();
                        return;
                      }

                      setShowAIEditDropdown(!showAIEditDropdown);
                    }}
                    label={compactToolbar ? aiTriggerLabel : `${aiTriggerLabel} description`}
                    variant="basic"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <StyledEditor
          ref={rte}
          error={error}
          disabled={disabled}
          maxWidth={maxWidth}
          modules={modules}
          value={toQuillHtml(value)}
          onChange={(html: string): void => onChange?.(fromQuillHtml(html))}
          {...props}
        />
      </div>
    </>
  );
};

export default RichTextEditor;
